import { useEffect, useRef, useState, MouseEvent } from 'react';
import Map, { MapRef, Marker, NavigationControl } from 'react-map-gl/mapbox';
import mapMarker from '../../assets/map-marker.png';
import hoveredMapMarker from '../../assets/hovered-map-marker.png';
import { PropertyDetailsResponseData } from '../../models/details.models';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import MapIcon from '@mui/icons-material/Map';
import { PropertySearchResTypeData } from '../../services/api';
import { RegridResponse } from '../../models/regrid.model';
import { ResultsAndMapLoading } from '../Results/ResultsAndMapLoading';
import 'mapbox-gl/dist/mapbox-gl.css';
import './mapbox-map.scss';

export const MAP_STARTING_LONGITUDE = -80.0942;
export const MAP_STARTING_LATITUDE = 26.9342;

interface MapboxMapProps {
  addressDetails?: PropertyDetailsResponseData;
  searchResponse?: PropertySearchResTypeData;
  regridData?: RegridResponse;
  dynamicResize?: boolean;
  latitude?: number;
  longitude?: number;
  isPending?: boolean;
}

export const MapboxMap = ({
  addressDetails,
  searchResponse,
  regridData,
  dynamicResize = false,
  latitude,
  longitude,
  isPending,
}: MapboxMapProps) => {
  const [viewState, setViewState] = useState({
    longitude: MAP_STARTING_LONGITUDE,
    latitude: MAP_STARTING_LATITUDE,
    zoom: 15,
  });

  const mapRef = useRef<MapRef>(null);

  const [mapViewType, setMapViewType] = useState<string | null>('map');
  const [hoveredMarker, setHoveredMarker] = useState<boolean | null>();

  useEffect(() => {
    if (
      mapRef.current &&
      (searchResponse || addressDetails || regridData) &&
      dynamicResize
    ) {
      mapRef.current.resize();
    }
  }, [searchResponse, addressDetails, regridData, dynamicResize]);

  useEffect(() => {
    if (latitude && longitude) {
      setViewState({
        longitude,
        latitude,
        zoom: 11,
      });
    } else {
      setViewState({
        longitude: addressDetails?.longitude || MAP_STARTING_LONGITUDE,
        latitude: addressDetails?.latitude || MAP_STARTING_LATITUDE,
        zoom: 15,
      });
    }
  }, [addressDetails, latitude, longitude]);

  const handleMapViewChange = (
    _event: MouseEvent<HTMLElement>,
    newMapView: string | null
  ) => {
    if (newMapView) {
      setMapViewType(newMapView);
    }
  };

  return (
    <div className="mapbox-map" data-testid="mapbox-map-wrapper">
      {!isPending ? (
        <div className="map-container">
          <Map
            data-mcm-mapbox-map
            {...viewState}
            mapboxAccessToken={import.meta.env.VITE_MAPBOX_PUBLIC_KEY as string}
            onMove={(evt) => setViewState(evt.viewState)}
            style={{
              width: '100%',
              height: '100%',
            }}
            ref={mapRef}
            mapStyle={
              mapViewType === 'map'
                ? 'mapbox://styles/mapbox/streets-v9'
                : 'mapbox://styles/mapbox/satellite-v9'
            }
          >
            <ToggleButtonGroup
              data-map-view-toggle
              value={mapViewType}
              exclusive
              onChange={handleMapViewChange}
              aria-label="text alignment"
            >
              <ToggleButton
                value="satellite"
                aria-label="left aligned"
                data-sat-view-button
              >
                <LanguageIcon />
                <span>Sat</span>
              </ToggleButton>
              <ToggleButton
                value="map"
                aria-label="centered"
                data-map-view-button
              >
                <MapIcon />
                <span>Map</span>
              </ToggleButton>
            </ToggleButtonGroup>

            <NavigationControl
              showZoom={true}
              showCompass
              position="bottom-right"
            />
            <div
              onMouseEnter={() => setHoveredMarker(true)}
              onMouseLeave={() => setHoveredMarker(false)}
            >
              <Marker
                longitude={addressDetails?.longitude || longitude || 0}
                latitude={addressDetails?.latitude || latitude || 0}
                anchor="bottom"
              >
                <img src={hoveredMarker ? hoveredMapMarker : mapMarker} />
              </Marker>
            </div>
          </Map>
        </div>
      ) : (
        <ResultsAndMapLoading />
      )}
    </div>
  );
};
